// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/finance/journal/JournalReport.tsx
// 
// این فایل را در همان مسیر بالا کپی کنید (روی فایل قبلی بنویسید)
// ============================================================

import { useState, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../../components/Layout";
import { journalAPI, accountsAPI } from "../../../services/api";
import PersianDatePicker, { gregorianToJalaliStr, todayGregorian, DualDate, gregorianToBothHtml } from "../../../utils/PersianDatePicker";
import { FileText, Printer, Calendar, Loader, Wallet } from "lucide-react";

// ⚠️ کدهای حساب‌های نقدی (دخل) که همیشه باید در انتهای گزارش جورنال نمایش داده شوند.
// این بخش فقط داده‌های موجود در حساب‌ها را می‌خواند (read-only) و هیچ داده مالی را تغییر نمی‌دهد.
const CASH_ACCOUNTS = [
  { code: "CA-1001-03", nameHint: "دخل-دالری", label: "دخل دالری" },
  { code: "CA-2001-02", nameHint: "دخل-افغانی", label: "دخل افغانی" },
];

// پیدا کردن حساب بر اساس کد (اولویت) یا نام (به عنوان جایگزین)
function findCashAccount(accounts: any[], code: string, nameHint: string) {
  if (!Array.isArray(accounts)) return null;
  return (
    accounts.find((a: any) => (a.code || a.account_code) === code) ||
    accounts.find((a: any) => String(a.name || a.account_name || "").includes(nameHint)) ||
    null
  );
}

// جمع ورودی/خروجی یک لیست تراکنش از پاسخ accounts.php (همان فرمول AccountsPage.tsx)
function sumDirectional(txns: any[]) {
  const list = Array.isArray(txns) ? txns : [];
  const totalIn = list.filter((t: any) => t.direction === "in").reduce((s: number, t: any) => s + Math.abs(parseFloat(t.amount || 0)), 0);
  const totalOut = list.filter((t: any) => t.direction === "out").reduce((s: number, t: any) => s + Math.abs(parseFloat(t.amount || 0)), 0);
  return { totalIn, totalOut };
}

// یک روز به تاریخ میلادی (YYYY-MM-DD) اضافه می‌کند
// یک روز به تاریخ میلادی (YYYY-MM-DD) اضافه می‌کند
function addDaysStr(dateStr: string, days: number) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, (m || 1) - 1, (d || 1) + days));
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

const fmt = (n: any) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

// رنگ استاندارد بر اساس علامت عدد: منفی=قرمز، مثبت=سبز، صفر=خاکستری خنثی
function signColor(n: number) {
  if (n < 0) return "#dc2626";
  if (n > 0) return "#059669";
  return "#64748b";
}
function signBg(n: number, dark: boolean) {
  if (n < 0) return dark ? "rgba(239,68,68,0.10)" : "#fef2f2";
  if (n > 0) return dark ? "rgba(16,185,129,0.10)" : "#ecfdf5";
  return dark ? "rgba(255,255,255,0.04)" : "#f8fafc";
}

// خواندن «شماره موتر» از رکورد معامله — با پشتیبانی از چند احتمال نام‌گذاری فیلد (بدون تغییر در داده)
function getVehicleNo(item: any): string {
  const v = item?.vehicle_number ?? item?.vehicle_no ?? item?.truck_number ?? item?.truck_plate
    ?? item?.car_number ?? item?.motor_number ?? item?.plate_number ?? item?.vehicle_plate ?? "";
  return v ? String(v) : "";
}

function yesterday() {
  return addDaysStr(todayGregorian(), -1);
}

// محاسبه جمع مبالغ به تفکیک ارز — با منطق هوشمند
function calcTotals(items: any[], amountField: string, currencyField: string, rateField?: string) {
  const totals: Record<string, { amount: number; rates: number[] }> = {};
  items.forEach(item => {
    const cur = item[currencyField] || "USD";
    const amt = Number(item[amountField]) || 0;
    const rate = rateField ? (Number(item[rateField]) || 1) : 1;
    if (!totals[cur]) totals[cur] = { amount: 0, rates: [] };
    totals[cur].amount += amt;
    if (rate !== 1) totals[cur].rates.push(rate);
  });
  return totals;
}

// محاسبه معادل دالر با منطق درست:
// - ارز USD: مستقیم جمع می‌شه
// - ارز غیر USD با نرخ: تقسیم بر نرخ (تبدیل به دالر)
// - ارزهایی که نرخ ندارند: جداگانه نمایش داده می‌شوند
function calcSmartUsdTotal(totals: Record<string, { amount: number; rates: number[] }>): { usd: number; nonConvertible: string[] } {
  let usd = 0;
  const nonConvertible: string[] = [];
  Object.entries(totals).forEach(([cur, { amount, rates }]) => {
    if (cur === "USD") {
      usd += amount;
    } else if (rates.length > 0) {
      // میانگین نرخ‌های وارد شده
      const avgRate = rates.reduce((s, r) => s + r, 0) / rates.length;
      usd += amount / avgRate;
    } else {
      // بدون نرخ → جداگانه نمایش بده
      nonConvertible.push(cur);
    }
  });
  return { usd, nonConvertible };
}

// رندر بخش جمع
function TotalRow({ totals, color, T, dark }: { totals: Record<string, { amount: number; rates: number[] }>; color: string; T: any; dark: boolean }) {
  const entries = Object.entries(totals);
  if (entries.length === 0) return null;

  const { usd, nonConvertible } = calcSmartUsdTotal(totals);
  const convertibleEntries = entries.filter(([cur]) => !nonConvertible.includes(cur));
  const nonConvertibleEntries = entries.filter(([cur]) => nonConvertible.includes(cur));
  const hasConvertible = convertibleEntries.length > 0;
  const showUsd = hasConvertible && (convertibleEntries.length > 1 || convertibleEntries[0]?.[0] !== "USD");

  return (
    <tr style={{ background: dark ? "rgba(139,92,246,0.12)" : "#f5f3ff", borderTop: "2px solid " + color }}>
      <td colSpan={3} style={{ padding: "10px 12px", fontWeight: 800, color: color, fontSize: 12, textAlign: "right" }}>
        🧮 جمع کل
      </td>
      <td colSpan={4} style={{ padding: "10px 12px" }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {/* ارزهای قابل تبدیل */}
          {convertibleEntries.map(([cur, { amount }]) => (
            <span key={cur} style={{
              background: dark ? "rgba(255,255,255,0.08)" : "#fff",
              border: "1.5px solid " + color,
              borderRadius: 8, padding: "4px 12px",
              fontSize: 12, fontWeight: 800, color: color,
            }}>
              {fmt(amount)} <span style={{ fontSize: 10, fontWeight: 600 }}>{cur}</span>
            </span>
          ))}
          {/* معادل دالر (فقط اگر چند ارز قابل تبدیل داریم یا غیر USD داریم) */}
          {showUsd && (
            <span style={{
              background: "linear-gradient(135deg,#10b981,#059669)",
              borderRadius: 8, padding: "4px 12px",
              fontSize: 12, fontWeight: 800, color: "#fff",
            }}>
              ≈ {fmt(usd)} <span style={{ fontSize: 10 }}>USD</span>
            </span>
          )}
          {/* ارزهای بدون نرخ — جداگانه نمایش */}
          {nonConvertibleEntries.length > 0 && convertibleEntries.length > 0 && (
            <span style={{ fontSize: 11, color: dark ? "rgba(255,255,255,0.4)" : "#94a3b8", padding: "0 4px" }}>|</span>
          )}
          {nonConvertibleEntries.map(([cur, { amount }]) => (
            <span key={cur} style={{
              background: dark ? "rgba(255,200,0,0.12)" : "#fefce8",
              border: "1.5px solid #f59e0b",
              borderRadius: 8, padding: "4px 12px",
              fontSize: 12, fontWeight: 800, color: "#f59e0b",
            }}>
              {fmt(amount)} <span style={{ fontSize: 10, fontWeight: 600 }}>{cur}</span>
            </span>
          ))}
        </div>
      </td>
      <td colSpan={2} />
    </tr>
  );
}

export default function JournalReport() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;

  const [mode, setMode] = useState<"today" | "yesterday" | "custom">("today");
  const [dateFrom, setDateFrom] = useState(todayGregorian());
  const [dateTo, setDateTo]     = useState(todayGregorian());
  const [loading, setLoading]   = useState(false);
  const [report, setReport]     = useState<any>(null);
  const [cashSummary, setCashSummary] = useState<any[]>([]);

  const getRange = useCallback(() => {
    if (mode === "today")     return { from: todayGregorian(), to: todayGregorian() };
    if (mode === "yesterday") return { from: yesterday(),      to: yesterday() };
    return { from: dateFrom, to: dateTo };
  }, [mode, dateFrom, dateTo]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setReport(null);
    setCashSummary([]);
    const range = getRange();

    const [purchases, sales, paymentsIn, paymentsOut, expenses, cogs, transfers, accountsList] = await Promise.all([
      journalAPI.listPurchases(),
      journalAPI.listSales(),
      journalAPI.listPaymentsIn(),
      journalAPI.listPaymentsOut(),
      journalAPI.listExpenses(),
      journalAPI.listCogs(),
      journalAPI.listTransfers(),
      accountsAPI.list().catch(() => null),
    ]);

    // فقط معاملات نهایی‌شده در گزارش می‌آیند — معاملاتی که هنوز «در انتظار» تایید/پرداخت هستند هیچ‌وقت نباید در گزارش جورنال بیایند
    const isFinal = (item: any) => String(item.status || "").toLowerCase() !== "pending";
    const filterByDate = (list: any[]) =>
      list.filter(item => item.date >= range.from && item.date <= range.to && isFinal(item));

    setReport({
      range,
      purchases:   filterByDate(purchases.success   ? purchases.purchases   || [] : []),
      sales:       filterByDate(sales.success        ? sales.sales           || [] : []),
      paymentsIn:  filterByDate(paymentsIn.success   ? paymentsIn.payments   || [] : []),
      paymentsOut: filterByDate(paymentsOut.success  ? paymentsOut.payments  || [] : []),
      expenses:    filterByDate(expenses.success     ? expenses.expenses     || [] : []),
      cogs:        filterByDate(cogs.success         ? cogs.cogs             || [] : []),
      transfers:   filterByDate(transfers.success    ? transfers.transfers   || [] : []),
    });

    // 🏦 حساب‌های نقدی (دخل دالری / دخل افغانی) — فقط یک عدد: موجودی بعد از آخرین تراکنشِ تا پایان بازه‌ی انتخابی.
    // نکته کلیدی: موجودی برگشتی از accounts.php همیشه «موجودی زنده‌ی همین لحظه» است، نه موجودی در پایان بازه‌ی انتخابی.
    // پس اگر بعد از تاریخ پایان بازه، تراکنش تازه‌تری هم ثبت شده باشد، اثرش را کم می‌کنیم.
    try {
      const accList = accountsList && accountsList.success ? (accountsList.accounts || accountsList.data || []) : [];
      const today = todayGregorian();
      const summaries = await Promise.all(
        CASH_ACCOUNTS.map(async (ca) => {
          const acc = findCashAccount(accList, ca.code, ca.nameHint);
          if (!acc) return { ...ca, found: false };

          const res = await accountsAPI.report(acc.id, range.to, range.to).catch(() => null);
          if (!res || !res.success) return { ...ca, found: false };

          const liveBalance = Number(res.account?.balance || 0);
          const currency = res.account?.currency || "";

          // موجودی واقعیِ پایان بازه = موجودی زنده‌ی فعلی منهای خالص تراکنش‌های ثبت‌شده‌ی بعد از پایان بازه تا امروز
          let balance = liveBalance;
          if (range.to < today) {
            const afterRes = await accountsAPI.report(acc.id, addDaysStr(range.to, 1), today).catch(() => null);
            const after = afterRes?.success ? sumDirectional(afterRes.transactions) : { totalIn: 0, totalOut: 0 };
            balance = liveBalance - (after.totalIn - after.totalOut);
          }

          return { ...ca, found: true, account: res.account, balance, currency };
        })
      );
      setCashSummary(summaries);
    } catch {
      setCashSummary(CASH_ACCOUNTS.map(ca => ({ ...ca, found: false })));
    }

    setLoading(false);
  }, [getRange]);

  const totalCount = report
    ? report.purchases.length + report.sales.length + report.paymentsIn.length +
      report.paymentsOut.length + report.expenses.length + report.cogs.length + report.transfers.length
    : 0;

  const handlePrint = () => {
    if (!report) return;
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(buildPrintHTML(report, cashSummary));
    win.document.close();
    setTimeout(() => win.print(), 400);
  };

  const cardSt: React.CSSProperties = {
    background: dark ? "rgba(255,255,255,0.03)" : "#fff",
    border: "1px solid " + (T.cardBorder || T.border),
    borderRadius: 14, padding: 20, marginBottom: 16,
  };

  const btnSt = (color: string, active = false): React.CSSProperties => ({
    display: "flex", alignItems: "center", gap: 6,
    padding: "9px 16px", borderRadius: 10, cursor: "pointer",
    fontSize: 12, fontWeight: 700, border: "none",
    fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
    background: active ? color : dark ? "rgba(255,255,255,0.06)" : "#f1f5f9",
    color: active ? "#fff" : T.subtext,
    transition: "all 0.2s",
    boxShadow: active ? "0 3px 12px " + color + "50" : "none",
  });

  const tdSt: React.CSSProperties = { padding: "9px 12px", color: T.text, borderBottom: "1px solid " + (T.cardBorder || T.border), verticalAlign: "top" };
  const thSt: React.CSSProperties = { padding: "10px 12px", textAlign: "right", fontWeight: 700, color: T.subtext, borderBottom: "1px solid " + (T.cardBorder || T.border), whiteSpace: "nowrap" as const, background: dark ? "rgba(255,255,255,0.05)" : "#f8fafc" };

  const [sectionFilters, setSectionFilters] = useState<Record<string, string>>({});

  const setFilter = (key: string, val: string) =>
    setSectionFilters(f => ({ ...f, [key]: val }));

  const applyFilter = (data: any[], filterKey: string) => {
    const q = (sectionFilters[filterKey] || "").toLowerCase().trim();
    if (!q) return data;
    return data.filter(item => JSON.stringify(item).toLowerCase().includes(q));
  };

  const renderSection = (title: string, color: string, rawData: any[], headers: string[], rowFn: (d: any, idx: number) => React.ReactElement, totalRow?: React.ReactElement) => {
    if (!rawData || rawData.length === 0) return null;
    const filterKey = title;
    const data = applyFilter(rawData, filterKey);
    const q = sectionFilters[filterKey] || "";
    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          <div style={{ width: 4, height: 20, borderRadius: 2, background: color }} />
          <span style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{title}</span>
          <span style={{ fontSize: 11, color: "#fff", background: color, borderRadius: 20, padding: "2px 10px", fontWeight: 700 }}>{data.length} / {rawData.length} معامله</span>
          {/* فیلتر */}
          <div style={{ flex: 1, minWidth: 200, position: "relative", marginRight: "auto" }}>
            <input
              value={q}
              onChange={e => setFilter(filterKey, e.target.value)}
              placeholder={`🔍 جستجو در ${title}...`}
              style={{
                width: "100%", padding: "6px 12px 6px 30px", borderRadius: 8, fontSize: 12,
                border: `1.5px solid ${q ? color : (dark ? "rgba(255,255,255,0.1)" : "#e2e8f0")}`,
                background: dark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                color: T.text, outline: "none", fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                boxSizing: "border-box" as const,
                boxShadow: q ? `0 0 0 2px ${color}30` : "none",
              }}
            />
            {q && (
              <button onClick={() => setFilter(filterKey, "")} style={{
                position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
                background: "none", border: "none", cursor: "pointer", color: T.subtext,
                fontSize: 14, lineHeight: 1, padding: 0,
              }}>×</button>
            )}
          </div>
        </div>
        {data.length === 0 ? (
          <div style={{ textAlign: "center", padding: "20px", color: T.subtext, fontSize: 12, borderRadius: 12, border: `1px dashed ${color}50` }}>
            هیچ نتیجه‌ای برای «{q}» یافت نشد
          </div>
        ) : (
          <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid " + (T.cardBorder || T.border) }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr>{headers.map(h => <th key={h} style={thSt}>{h}</th>)}</tr></thead>
              <tbody>
                {data.map((item: any, idx: number) => rowFn(item, idx))}
                {totalRow}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <FileText size={18} color="#fff" />
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>گزارش جورنال</div>
          <div style={{ fontSize: 11, color: T.subtext }}>گزارش کامل تمامی معاملات بر اساس تاریخ</div>
        </div>
      </div>

      <div style={cardSt}>
        <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 14 }}>
          <Calendar size={13} style={{ display: "inline", marginLeft: 6 }} />انتخاب بازه زمانی
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          {[{ id: "today", label: "امروز" }, { id: "yesterday", label: "دیروز" }, { id: "custom", label: "بازه دلخواه" }].map(m => (
            <button key={m.id} onClick={() => setMode(m.id as any)} style={btnSt("#8b5cf6", mode === m.id)}>{m.label}</button>
          ))}
        </div>
        {mode === "custom" ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><div style={{ fontSize: 11, color: T.subtext, marginBottom: 5, fontWeight: 600 }}>از تاریخ</div><PersianDatePicker value={dateFrom} onChange={setDateFrom} /></div>
            <div><div style={{ fontSize: 11, color: T.subtext, marginBottom: 5, fontWeight: 600 }}>تا تاریخ</div><PersianDatePicker value={dateTo} onChange={setDateTo} /></div>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: T.subtext, background: dark ? "rgba(139,92,246,0.1)" : "#f5f3ff", padding: "8px 14px", borderRadius: 8, display: "inline-block" }}>
            📅 {mode === "today" ? gregorianToJalaliStr(todayGregorian()) : gregorianToJalaliStr(yesterday())}
          </div>
        )}
        <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
          <button onClick={loadReport} disabled={loading} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 24px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "'Vazirmatn','Segoe UI',sans-serif", opacity: loading ? 0.7 : 1 }}>
            {loading ? <Loader size={14} /> : <FileText size={14} />}
            {loading ? "در حال بارگذاری..." : "تهیه گزارش"}
          </button>
          {report && totalCount > 0 && (
            <button onClick={handlePrint} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
              <Printer size={14} /> پرینت گزارش
            </button>
          )}
        </div>
      </div>

      {report && (
        <div>
          {/* کارت‌های خلاصه */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(130px,1fr))", gap: 10, marginBottom: 20 }}>
            {[
              { label: "خرید", color: "#f59e0b", count: report.purchases.length },
              { label: "فروش", color: "#10b981", count: report.sales.length },
              { label: "دریافت پول", color: "#3b82f6", count: report.paymentsIn.length },
              { label: "پرداخت پول", color: "#f97316", count: report.paymentsOut.length },
              { label: "مصارف", color: "#ef4444", count: report.expenses.length },
              { label: "COGS", color: "#8b5cf6", count: report.cogs.length },
              { label: "انتقال پول", color: "#06b6d4", count: report.transfers.length },
            ].map(t => (
              <div key={t.label} style={{ background: dark ? "rgba(255,255,255,0.03)" : "#fff", border: "1px solid " + (T.cardBorder || T.border), borderRadius: 12, padding: "12px 14px", borderTop: "3px solid " + t.color }}>
                <div style={{ fontSize: 11, color: T.subtext, marginBottom: 4 }}>{t.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: t.color }}>{t.count}</div>
                <div style={{ fontSize: 10, color: T.subtext }}>معامله</div>
              </div>
            ))}
          </div>

          {totalCount === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: T.subtext, fontSize: 13 }}>هیچ معامله‌ای در این بازه زمانی ثبت نشده</div>
          ) : (
            <div>
              {/* خرید */}
              {renderSection("خرید", "#f59e0b", report.purchases,
                ["شماره سند","تاریخ","فروشنده","جزئیات آیتم‌ها","مبلغ کل","تخفیف","خالص","ارز","شماره موتر","توضیحات"],
                (item, idx) => (
                  <tr key={item.id} style={{ background: idx % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.015)" : "#fafafa") }}>
                    <td style={tdSt}><span style={{ color: "#f59e0b", fontWeight: 700 }}>{item.serial}</span></td>
                    <td style={tdSt}><DualDate date={item.date} fontSize={11} color={"inherit"} /></td>
                    <td style={tdSt}>{item.vendor_name}</td>
                    <td style={{ ...tdSt, maxWidth: 240 }}>{(item.items || []).map((it: any, i: number) => <div key={i} style={{ fontSize: 11, marginBottom: 3, padding: "3px 6px", borderRadius: 6, background: dark ? "rgba(255,255,255,0.04)" : "#f1f5f9" }}>📦 {it.product_name} — {it.quantity} {it.unit} × {fmt(it.unit_price)} = <strong>{fmt(it.total_price)}</strong>{it.warehouse_name ? <span style={{ color: "#10b981" }}> ({it.warehouse_name})</span> : null}</div>)}</td>
                    <td style={tdSt}>{fmt(item.total_amount || item.net_amount)}</td>
                    <td style={{ ...tdSt, color: "#ef4444" }}>{fmt(item.discount || 0)}</td>
                    <td style={{ ...tdSt, fontWeight: 800, color: "#f59e0b" }}>{fmt(item.net_amount)}</td>
                    <td style={tdSt}>{item.vendor_currency}</td>
                    <td style={tdSt}>{getVehicleNo(item) || "—"}</td>
                    <td style={{ ...tdSt, fontSize: 11, color: T.subtext }}>{item.notes || "—"}</td>
                  </tr>
                ),
                <TotalRow key="t" totals={calcTotals(report.purchases, "net_amount", "vendor_currency")} color="#f59e0b" T={T} dark={dark} />
              )}

              {/* فروش */}
              {renderSection("فروش", "#10b981", report.sales,
                ["شماره سند","تاریخ","مشتری","جزئیات آیتم‌ها","مبلغ کل","تخفیف","خالص","ارز","شماره موتر","توضیحات"],
                (item, idx) => (
                  <tr key={item.id} style={{ background: idx % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.015)" : "#fafafa") }}>
                    <td style={tdSt}><span style={{ color: "#10b981", fontWeight: 700 }}>{item.serial}</span></td>
                    <td style={tdSt}><DualDate date={item.date} fontSize={11} color={"inherit"} /></td>
                    <td style={tdSt}>{item.customer_name}</td>
                    <td style={{ ...tdSt, maxWidth: 240 }}>{(item.items || []).map((it: any, i: number) => <div key={i} style={{ fontSize: 11, marginBottom: 3, padding: "3px 6px", borderRadius: 6, background: dark ? "rgba(255,255,255,0.04)" : "#f1f5f9" }}>📦 {it.product_name} — {it.quantity} {it.unit} × {fmt(it.unit_price)} = <strong>{fmt(it.total_price)}</strong>{it.warehouse_name ? <span style={{ color: "#10b981" }}> ({it.warehouse_name})</span> : null}</div>)}</td>
                    <td style={tdSt}>{fmt(item.total_amount || item.net_amount)}</td>
                    <td style={{ ...tdSt, color: "#ef4444" }}>{fmt(item.discount || 0)}</td>
                    <td style={{ ...tdSt, fontWeight: 800, color: "#10b981" }}>{fmt(item.net_amount)}</td>
                    <td style={tdSt}>{item.customer_currency}</td>
                    <td style={tdSt}>{getVehicleNo(item) || "—"}</td>
                    <td style={{ ...tdSt, fontSize: 11, color: T.subtext }}>{item.notes || "—"}</td>
                  </tr>
                ),
                <TotalRow key="t" totals={calcTotals(report.sales, "net_amount", "customer_currency")} color="#10b981" T={T} dark={dark} />
              )}

              {/* دریافت پول */}
              {renderSection("دریافت پول", "#3b82f6", report.paymentsIn,
                ["شماره سند","تاریخ","مشتری","مبلغ","ارز","نرخ تبدیل","حساب","توضیحات"],
                (item, idx) => (
                  <tr key={item.id} style={{ background: idx % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.015)" : "#fafafa") }}>
                    <td style={tdSt}><span style={{ color: "#3b82f6", fontWeight: 700 }}>{item.serial}</span></td>
                    <td style={tdSt}><DualDate date={item.date} fontSize={11} color={"inherit"} /></td>
                    <td style={tdSt}>{item.customer_name}</td>
                    <td style={{ ...tdSt, fontWeight: 800, color: "#3b82f6" }}>{fmt(item.receipt_amount)}</td>
                    <td style={tdSt}>{item.receipt_currency}</td>
                    <td style={tdSt}>{item.exchange_rate}</td>
                    <td style={tdSt}>{item.account_name}</td>
                    <td style={{ ...tdSt, fontSize: 11, color: T.subtext }}>{item.notes || "—"}</td>
                  </tr>
                ),
                <TotalRow key="t" totals={calcTotals(report.paymentsIn, "receipt_amount", "receipt_currency", "exchange_rate")} color="#3b82f6" T={T} dark={dark} />
              )}

              {/* پرداخت پول */}
              {renderSection("پرداخت پول", "#f97316", report.paymentsOut,
                ["شماره سند","تاریخ","فروشنده","مبلغ پرداختی","ارز","معادل دالر","نرخ تبدیل","حساب پرداختی","توضیحات"],
                (item, idx) => {
                  const isNonUsd = item.payment_currency && item.payment_currency !== "USD";
                  const rate = parseFloat(item.exchange_rate || 1);
                  const usdEquiv = isNonUsd ? (item.payment_amount / rate) : null;
                  return (
                    <tr key={item.id} style={{ background: idx % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.015)" : "#fafafa") }}>
                      <td style={tdSt}><span style={{ color: "#f97316", fontWeight: 700 }}>{item.serial}</span></td>
                      <td style={tdSt}><DualDate date={item.date} fontSize={11} color={"inherit"} /></td>
                      <td style={tdSt}>{item.vendor_name}</td>
                      <td style={{ ...tdSt, fontWeight: 800, color: "#f97316" }}>{fmt(item.payment_amount)}</td>
                      <td style={tdSt}><span style={{ background: "#fff7ed", color: "#f97316", padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700 }}>{item.payment_currency}</span></td>
                      <td style={{ ...tdSt, fontWeight: 700, color: "#10b981" }}>
                        {usdEquiv !== null ? `$${fmt(usdEquiv)}` : <span style={{ color: "#10b981", fontWeight: 700 }}>$ {fmt(item.payment_amount)}</span>}
                      </td>
                      <td style={{ ...tdSt, color: isNonUsd ? T.text : T.subtext }}>
                        {isNonUsd ? <span style={{ background: dark ? "rgba(249,115,22,0.15)" : "#fff7ed", color: "#f97316", padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700 }}>× {rate}</span> : "—"}
                      </td>
                      <td style={{ ...tdSt, fontWeight: 600, color: "#3b82f6" }}>
                        <span style={{ background: dark ? "rgba(59,130,246,0.15)" : "#eff6ff", color: "#3b82f6", padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
                          🏦 {item.account_name || "—"}
                        </span>
                      </td>
                      <td style={{ ...tdSt, fontSize: 11, color: T.subtext }}>{item.notes || "—"}</td>
                    </tr>
                  );
                },
                <TotalRow key="t" totals={calcTotals(report.paymentsOut, "payment_amount", "payment_currency", "exchange_rate")} color="#f97316" T={T} dark={dark} />
              )}

              {/* مصارف */}
              {renderSection("ثبت مصارف", "#ef4444", report.expenses,
                ["شماره سند","تاریخ","دریافت‌کننده","دسته‌بندی","مبلغ","ارز","وضعیت","توضیحات"],
                (item, idx) => (
                  <tr key={item.id} style={{ background: idx % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.015)" : "#fafafa") }}>
                    <td style={tdSt}><span style={{ color: "#ef4444", fontWeight: 700 }}>{item.serial}</span></td>
                    <td style={tdSt}><DualDate date={item.date} fontSize={11} color={"inherit"} /></td>
                    <td style={tdSt}>{item.recipient_name}</td>
                    <td style={tdSt}>{item.category_name || "—"}</td>
                    <td style={{ ...tdSt, fontWeight: 800, color: "#ef4444" }}>{fmt(item.amount)}</td>
                    <td style={tdSt}>{item.currency}</td>
                    <td style={tdSt}><span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: item.status === "paid" ? "#d1fae5" : "#fef3c7", color: item.status === "paid" ? "#059669" : "#d97706" }}>{item.status === "paid" ? "پرداخت شده" : "در انتظار"}</span></td>
                    <td style={{ ...tdSt, fontSize: 11, color: T.subtext }}>{item.notes || "—"}</td>
                  </tr>
                ),
                <TotalRow key="t" totals={calcTotals(report.expenses, "amount", "currency", "exchange_rate")} color="#ef4444" T={T} dark={dark} />
              )}

              {/* COGS */}
              {renderSection("هزینه اجناس / COGS", "#8b5cf6", report.cogs,
                ["شماره سند","تاریخ","دریافت‌کننده","دسته‌بندی","مبلغ","ارز","معادل دالر","نرخ تبدیل","حساب پرداختی","وضعیت","شماره موتر","توضیحات"],
                (item, idx) => {
                  const isNonUsd = item.currency && item.currency !== "USD";
                  const rate = parseFloat(item.exchange_rate || 1);
                  const usdEquiv = isNonUsd ? (item.amount / rate) : null;
                  return (
                    <tr key={item.id} style={{ background: idx % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.015)" : "#fafafa") }}>
                      <td style={tdSt}><span style={{ color: "#8b5cf6", fontWeight: 700 }}>{item.serial}</span></td>
                      <td style={tdSt}><DualDate date={item.date} fontSize={11} color={"inherit"} /></td>
                      <td style={tdSt}>{item.recipient_name}</td>
                      <td style={tdSt}>{item.category_name || "—"}</td>
                      <td style={{ ...tdSt, fontWeight: 800, color: "#8b5cf6" }}>{fmt(item.amount)}</td>
                      <td style={tdSt}><span style={{ background: dark ? "rgba(139,92,246,0.15)" : "#f5f3ff", color: "#8b5cf6", padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700 }}>{item.currency}</span></td>
                      <td style={{ ...tdSt, fontWeight: 700, color: "#10b981" }}>
                        {usdEquiv !== null ? `$${fmt(usdEquiv)}` : <span style={{ color: "#10b981" }}>$ {fmt(item.amount)}</span>}
                      </td>
                      <td style={{ ...tdSt, color: isNonUsd ? T.text : T.subtext }}>
                        {isNonUsd ? <span style={{ background: dark ? "rgba(139,92,246,0.15)" : "#f5f3ff", color: "#8b5cf6", padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700 }}>× {rate}</span> : "—"}
                      </td>
                      <td style={{ ...tdSt, fontWeight: 600 }}>
                        {item.account_name
                          ? <span style={{ background: dark ? "rgba(59,130,246,0.15)" : "#eff6ff", color: "#3b82f6", padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700 }}>🏦 {item.account_name}</span>
                          : <span style={{ color: T.subtext, fontSize: 11 }}>در انتظار پرداخت</span>
                        }
                      </td>
                      <td style={tdSt}><span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: item.status === "paid" ? "#d1fae5" : "#fef3c7", color: item.status === "paid" ? "#059669" : "#d97706" }}>{item.status === "paid" ? "پرداخت شده" : "در انتظار"}</span></td>
                      <td style={tdSt}>{getVehicleNo(item) || "—"}</td>
                      <td style={{ ...tdSt, fontSize: 11, color: T.subtext }}>{item.notes || "—"}</td>
                    </tr>
                  );
                },
                <TotalRow key="t" totals={calcTotals(report.cogs, "amount", "currency", "exchange_rate")} color="#8b5cf6" T={T} dark={dark} />
              )}

              {/* انتقال پول */}
              {renderSection("انتقال پول", "#06b6d4", report.transfers,
                ["شماره سند","تاریخ","از حساب","به حساب","مبلغ مبدا","مبلغ مقصد","کمیسیون","توضیحات"],
                (item, idx) => (
                  <tr key={item.id} style={{ background: idx % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.015)" : "#fafafa") }}>
                    <td style={tdSt}><span style={{ color: "#06b6d4", fontWeight: 700 }}>{item.serial}</span></td>
                    <td style={tdSt}><DualDate date={item.date} fontSize={11} color={"inherit"} /></td>
                    <td style={tdSt}>{item.from_account_name}</td>
                    <td style={tdSt}>{item.to_account_name}</td>
                    <td style={{ ...tdSt, fontWeight: 800, color: "#06b6d4" }}>{fmt(item.from_amount)}</td>
                    <td style={{ ...tdSt, fontWeight: 800, color: "#10b981" }}>{fmt(item.to_amount)}</td>
                    <td style={tdSt}>{fmt(item.commission || 0)}</td>
                    <td style={{ ...tdSt, fontSize: 11, color: T.subtext }}>{item.notes || "—"}</td>
                  </tr>
                ),
                <TotalRow key="t" totals={calcTotals(report.transfers, "from_amount", "from_currency")} color="#06b6d4" T={T} dark={dark} />
              )}
            </div>
          )}

          {/* 🏦 خلاصه حساب‌های نقدی (دخل دالری / دخل افغانی) — همیشه در انتهای گزارش، مستقل از فیلتر معاملات */}
          <div style={{ marginTop: 28 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <div style={{ width: 4, height: 20, borderRadius: 2, background: "#0ea5e9" }} />
              <span style={{ fontSize: 13, fontWeight: 800, color: T.text }}>خلاصه حساب‌های نقدی (دخل)</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 12 }}>
              {cashSummary.map((cs: any) => (
                <div key={cs.code} style={{ background: dark ? "rgba(14,165,233,0.06)" : "#f0f9ff", border: "1px solid " + (dark ? "rgba(14,165,233,0.3)" : "#bae6fd"), borderRadius: 14, padding: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <Wallet size={15} color="#0ea5e9" />
                    <span style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{cs.label}</span>
                    <span style={{ fontSize: 10, color: T.subtext, fontFamily: "monospace" }}>{cs.code}</span>
                  </div>
                  {!cs.found ? (
                    <div style={{ fontSize: 11, color: "#f59e0b" }}>⚠ این حساب در سیستم یافت نشد</div>
                  ) : (
                    <div style={{ background: signBg(cs.balance, dark), border: "1.5px solid " + signColor(cs.balance), borderRadius: 10, padding: "14px 16px", textAlign: "center" }}>
                      <div style={{ fontSize: 22, fontWeight: 900, color: signColor(cs.balance) }}>{fmt(cs.balance)} {cs.currency}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== HTML پرینت =====
function buildTotalHTML(totals: Record<string, { amount: number; rates: number[] }>, color: string, colCount: number = 9): string {
  const entries = Object.entries(totals);
  if (entries.length === 0) return "";
  const { usd, nonConvertible } = calcSmartUsdTotal(totals);
  const fmt2 = (n: number) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  const convertibleEntries = entries.filter(([cur]) => !nonConvertible.includes(cur));
  const nonConvertibleEntries = entries.filter(([cur]) => nonConvertible.includes(cur));
  const showUsd = convertibleEntries.length > 0 && (convertibleEntries.length > 1 || convertibleEntries[0]?.[0] !== "USD");
  const labelSpan = Math.max(1, Math.min(3, colCount - 1));
  const valueSpan = Math.max(1, colCount - labelSpan);

  return `<tr class="total-row" style="border-top:2px solid ${color}">
    <td colspan="${labelSpan}" style="padding:10px 12px;font-weight:800;color:${color};font-size:12px">🧮 جمع کل</td>
    <td colspan="${valueSpan}" style="padding:10px 12px">
      <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center">
        ${convertibleEntries.map(([cur, { amount }]) => `<span style="background:#f8fafc;border:1.5px solid ${color};border-radius:8px;padding:4px 12px;font-size:12px;font-weight:800;color:${color}">${fmt2(amount)} <span style="font-size:10px;font-weight:600">${cur}</span></span>`).join("")}
        ${showUsd ? `<span style="background:linear-gradient(135deg,#10b981,#059669);border-radius:8px;padding:4px 12px;font-size:12px;font-weight:800;color:#fff">≈ ${fmt2(usd)} <span style="font-size:10px">USD</span></span>` : ""}
        ${nonConvertibleEntries.length > 0 && convertibleEntries.length > 0 ? `<span style="color:#94a3b8;padding:0 4px">|</span>` : ""}
        ${nonConvertibleEntries.map(([cur, { amount }]) => `<span style="background:#fefce8;border:1.5px solid #f59e0b;border-radius:8px;padding:4px 12px;font-size:12px;font-weight:800;color:#f59e0b">${fmt2(amount)} <span style="font-size:10px;font-weight:600">${cur}</span></span>`).join("")}
      </div>
    </td>
  </tr>`;
}

function buildCashSummaryHTML(cashSummary: any[]): string {
  if (!cashSummary || cashSummary.length === 0) return "";
  const fmt2 = (n: any) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
  const cards = cashSummary.map((cs: any) => {
    if (!cs.found) {
      return `<div class="cash-card"><div class="cash-title">${cs.label} <span class="cash-code">${cs.code}</span></div><div class="cash-missing">⚠ این حساب در سیستم یافت نشد</div></div>`;
    }
    return `<div class="cash-card">
      <div class="cash-title">${cs.label} <span class="cash-code">${cs.code}</span></div>
      <div class="cash-single ${cs.balance < 0 ? "neg" : "pos"}">${fmt2(cs.balance)} ${cs.currency || ""}</div>
    </div>`;
  }).join("");
  return `<div class="section" style="page-break-inside:avoid">
    <div class="section-header" style="border-right:4px solid #0ea5e9"><span style="color:#0ea5e9">خلاصه حساب‌های نقدی (دخل)</span></div>
    <div class="cash-wrap">${cards}</div>
  </div>`;
}

function buildPrintHTML(report: any, cashSummary?: any[]): string {
  const fmt2 = (n: any) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
  const range = report.range;
  const fromJ = gregorianToJalaliStr(range.from);
  const toJ   = gregorianToJalaliStr(range.to);
  const title = fromJ === toJ ? "گزارش جورنال — " + fromJ : "گزارش جورنال — " + fromJ + " تا " + toJ;
  const total = report.purchases.length + report.sales.length + report.paymentsIn.length + report.paymentsOut.length + report.expenses.length + report.cogs.length + report.transfers.length;

  const buildTable = (sectionTitle: string, color: string, headers: string[], rows: string, totals: Record<string, { amount: number; rates: number[] }>) => {
    if (!rows) return "";
    return `<div class="section">
      <div class="section-header" style="border-right:4px solid ${color}">
        <span style="color:${color}">${sectionTitle}</span>
        <span class="section-badge" style="background:${color}">${rows.split("<tr").length - 1} معامله</span>
      </div>
      <table><thead><tr>${headers.map(h => `<th>${h}</th>`).join("")}</tr></thead>
      <tbody>${rows}${buildTotalHTML(totals, color, headers.length)}</tbody></table></div>`;
  };

  const purchaseRows = report.purchases.map((item: any, idx: number) => `<tr class="${idx % 2 === 1 ? "odd" : "even"}">
    <td class="serial" style="color:#f59e0b">${item.serial}</td><td>${gregorianToBothHtml(item.date)}</td><td>${item.vendor_name || ""}</td>
    <td class="items-cell">${(item.items || []).map((it: any) => `<div class="item-row">📦 ${it.product_name} — ${it.quantity} ${it.unit} × ${fmt2(it.unit_price)} = <b>${fmt2(it.total_price)}</b>${it.warehouse_name ? ` (${it.warehouse_name})` : ""}</div>`).join("")}</td>
    <td class="num">${fmt2(item.total_amount || item.net_amount)}</td><td class="num red">${fmt2(item.discount || 0)}</td>
    <td class="num bold" style="color:#f59e0b">${fmt2(item.net_amount)}</td><td>${item.vendor_currency || ""}</td><td>${getVehicleNo(item) || "—"}</td><td class="notes">${item.notes || ""}</td></tr>`).join("");

  const saleRows = report.sales.map((item: any, idx: number) => `<tr class="${idx % 2 === 1 ? "odd" : "even"}">
    <td class="serial" style="color:#10b981">${item.serial}</td><td>${gregorianToBothHtml(item.date)}</td><td>${item.customer_name || ""}</td>
    <td class="items-cell">${(item.items || []).map((it: any) => `<div class="item-row">📦 ${it.product_name} — ${it.quantity} ${it.unit} × ${fmt2(it.unit_price)} = <b>${fmt2(it.total_price)}</b>${it.warehouse_name ? ` (${it.warehouse_name})` : ""}</div>`).join("")}</td>
    <td class="num">${fmt2(item.total_amount || item.net_amount)}</td><td class="num red">${fmt2(item.discount || 0)}</td>
    <td class="num bold" style="color:#10b981">${fmt2(item.net_amount)}</td><td>${item.customer_currency || ""}</td><td>${getVehicleNo(item) || "—"}</td><td class="notes">${item.notes || ""}</td></tr>`).join("");

  const payInRows = report.paymentsIn.map((item: any, idx: number) => `<tr class="${idx % 2 === 1 ? "odd" : "even"}">
    <td class="serial" style="color:#3b82f6">${item.serial}</td><td>${gregorianToBothHtml(item.date)}</td><td>${item.customer_name || ""}</td>
    <td class="num bold" style="color:#3b82f6">${fmt2(item.receipt_amount)}</td><td>${item.receipt_currency || ""}</td><td>${item.exchange_rate || 1}</td>
    <td>${item.account_name || ""}</td><td class="notes">${item.notes || ""}</td></tr>`).join("");

  const payOutRows = report.paymentsOut.map((item: any, idx: number) => {
    const isNonUsd = item.payment_currency && item.payment_currency !== "USD";
    const rate = parseFloat(item.exchange_rate || 1);
    const usdEquiv = isNonUsd ? (item.payment_amount / rate) : item.payment_amount;
    return `<tr class="${idx % 2 === 1 ? "odd" : "even"}">
    <td class="serial" style="color:#f97316">${item.serial}</td>
    <td>${gregorianToBothHtml(item.date)}</td>
    <td>${item.vendor_name || ""}</td>
    <td class="num bold" style="color:#f97316">${fmt2(item.payment_amount)}</td>
    <td><span style="background:#fff7ed;color:#f97316;padding:2px 8px;border-radius:6px;font-size:10px;font-weight:700">${item.payment_currency || ""}</span></td>
    <td class="num bold green">$${fmt2(usdEquiv)}</td>
    <td>${isNonUsd ? `<span style="background:#fff7ed;color:#f97316;padding:2px 6px;border-radius:6px;font-size:10px;font-weight:700">× ${rate}</span>` : "—"}</td>
    <td><span style="background:#eff6ff;color:#3b82f6;padding:2px 8px;border-radius:6px;font-size:10px;font-weight:700">🏦 ${item.account_name || "—"}</span></td>
    <td class="notes">${item.notes || ""}</td></tr>`;
  }).join("");

  const expRows = report.expenses.map((item: any, idx: number) => `<tr class="${idx % 2 === 1 ? "odd" : "even"}">
    <td class="serial" style="color:#ef4444">${item.serial}</td><td>${gregorianToBothHtml(item.date)}</td><td>${item.recipient_name || ""}</td>
    <td>${item.category_name || ""}</td><td class="num bold" style="color:#ef4444">${fmt2(item.amount)}</td><td>${item.currency || ""}</td>
    <td><span class="status ${item.status === "paid" ? "paid" : "pending"}">${item.status === "paid" ? "پرداخت شده" : "در انتظار"}</span></td><td class="notes">${item.notes || ""}</td></tr>`).join("");

  const cogsRows = report.cogs.map((item: any, idx: number) => {
    const isNonUsd = item.currency && item.currency !== "USD";
    const rate = parseFloat(item.exchange_rate || 1);
    const usdEquiv = isNonUsd ? (item.amount / rate) : item.amount;
    return `<tr class="${idx % 2 === 1 ? "odd" : "even"}">
    <td class="serial" style="color:#8b5cf6">${item.serial}</td>
    <td>${gregorianToBothHtml(item.date)}</td>
    <td>${item.recipient_name || ""}</td>
    <td>${item.category_name || ""}</td>
    <td class="num bold" style="color:#8b5cf6">${fmt2(item.amount)}</td>
    <td><span style="background:#f5f3ff;color:#8b5cf6;padding:2px 8px;border-radius:6px;font-size:10px;font-weight:700">${item.currency || ""}</span></td>
    <td class="num bold green">$${fmt2(usdEquiv)}</td>
    <td>${isNonUsd ? `<span style="background:#f5f3ff;color:#8b5cf6;padding:2px 6px;border-radius:6px;font-size:10px;font-weight:700">× ${rate}</span>` : "—"}</td>
    <td>${item.account_name ? `<span style="background:#eff6ff;color:#3b82f6;padding:2px 8px;border-radius:6px;font-size:10px;font-weight:700">🏦 ${item.account_name}</span>` : '<span style="color:#d97706;font-size:10px">در انتظار</span>'}</td>
    <td><span class="status ${item.status === "paid" ? "paid" : "pending"}">${item.status === "paid" ? "پرداخت شده" : "در انتظار"}</span></td>
    <td>${getVehicleNo(item) || "—"}</td>
    <td class="notes">${item.notes || ""}</td></tr>`;
  }).join("");

  const trfRows = report.transfers.map((item: any, idx: number) => `<tr class="${idx % 2 === 1 ? "odd" : "even"}">
    <td class="serial" style="color:#06b6d4">${item.serial}</td><td>${gregorianToBothHtml(item.date)}</td><td>${item.from_account_name || ""}</td>
    <td>${item.to_account_name || ""}</td><td class="num bold" style="color:#06b6d4">${fmt2(item.from_amount)}</td>
    <td class="num bold green">${fmt2(item.to_amount)}</td><td class="num">${fmt2(item.commission || 0)}</td><td class="notes">${item.notes || ""}</td></tr>`).join("");

  const sections = [
    purchaseRows && buildTable("خرید", "#f59e0b", ["شماره سند","تاریخ","فروشنده","جزئیات آیتم‌ها","مبلغ کل","تخفیف","خالص","ارز","شماره موتر","توضیحات"], purchaseRows, calcTotals(report.purchases, "net_amount", "vendor_currency")),
    saleRows     && buildTable("فروش", "#10b981", ["شماره سند","تاریخ","مشتری","جزئیات آیتم‌ها","مبلغ کل","تخفیف","خالص","ارز","شماره موتر","توضیحات"], saleRows, calcTotals(report.sales, "net_amount", "customer_currency")),
    payInRows    && buildTable("دریافت پول", "#3b82f6", ["شماره سند","تاریخ","مشتری","مبلغ","ارز","نرخ تبدیل","حساب","توضیحات"], payInRows, calcTotals(report.paymentsIn, "receipt_amount", "receipt_currency", "exchange_rate")),
    payOutRows   && buildTable("پرداخت پول", "#f97316", ["شماره سند","تاریخ","فروشنده","مبلغ پرداختی","ارز","معادل دالر","نرخ تبدیل","حساب پرداختی","توضیحات"], payOutRows, calcTotals(report.paymentsOut, "payment_amount", "payment_currency", "exchange_rate")),
    expRows      && buildTable("ثبت مصارف", "#ef4444", ["شماره سند","تاریخ","دریافت‌کننده","دسته‌بندی","مبلغ","ارز","وضعیت","توضیحات"], expRows, calcTotals(report.expenses, "amount", "currency", "exchange_rate")),
    cogsRows     && buildTable("هزینه اجناس / COGS", "#8b5cf6", ["شماره سند","تاریخ","دریافت‌کننده","دسته‌بندی","مبلغ","ارز","معادل دالر","نرخ تبدیل","حساب پرداختی","وضعیت","شماره موتر","توضیحات"], cogsRows, calcTotals(report.cogs, "amount", "currency", "exchange_rate")),
    trfRows      && buildTable("انتقال پول", "#06b6d4", ["شماره سند","تاریخ","از حساب","به حساب","مبلغ مبدا","مبلغ مقصد","کمیسیون","توضیحات"], trfRows, calcTotals(report.transfers, "from_amount", "from_currency")),
  ].filter(Boolean).join("");

  return `<!DOCTYPE html><html dir="rtl" lang="fa"><head><meta charset="utf-8"/><title>${title}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Vazirmatn:wght@300;400;600;700;800;900&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
@page{size:A4 portrait;margin:8mm 7mm}
body{font-family:'Vazirmatn','Tahoma',sans-serif;direction:rtl;background:#fff;color:#1e1b2e;font-size:8.5px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.page{padding:0}
/* هدر */
.header{display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;padding-bottom:10px;border-bottom:3px solid #8b5cf6}
.logo-box{width:42px;height:42px;background:linear-gradient(135deg,#8b5cf6,#6d28d9);border-radius:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.title{font-size:18px;font-weight:900;color:#1e1b2e}
.subtitle{font-size:11px;color:#64748b;margin-top:2px}
.meta{text-align:left;font-size:10px;color:#64748b;line-height:1.9}
.meta strong{color:#1e1b2e;font-weight:700}
/* خلاصه */
.summary{display:flex;gap:6px;margin-bottom:12px}
.sum-item{flex:1;text-align:center;padding:8px 6px;border-radius:8px;border:1px solid #e2e8f0}
.sum-count{font-size:17px;font-weight:900}
.sum-label{font-size:9px;color:#64748b;margin-top:2px;font-weight:600}
/* سکشن */
.section{margin-bottom:12px}
.section-header{display:flex;align-items:center;gap:8px;padding:7px 12px;border-radius:8px 8px 0 0;background:#f8fafc;border:1px solid #e2e8f0;border-bottom:none;page-break-after:avoid}
.section-header span{font-size:12px;font-weight:800}
.section-badge{font-size:10px;font-weight:700;padding:2px 10px;border-radius:20px;color:#fff}
/* جدول */
table{width:100%;border-collapse:collapse;border:1px solid #e2e8f0;border-top:none;font-size:8px;table-layout:auto}
thead tr{background:linear-gradient(135deg,#1e293b,#334155)}
th{padding:5px 5px;text-align:right;font-weight:700;color:#f1f5f9;font-size:7.5px;overflow-wrap:break-word;white-space:nowrap}
td{padding:4px 5px;border-bottom:1px solid #f1f5f9;vertical-align:top;overflow-wrap:break-word;word-break:break-word}
tr:last-child td{border-bottom:none}
tr.even{background:#fff}
tr.odd{background:#f8fafc}
.total-row td{background:linear-gradient(135deg,#faf5ff,#f5f3ff);border-top:1.5px solid #8b5cf680;font-weight:700}
/* فیلدها */
.serial{font-weight:700;font-family:monospace;font-size:7.5px;overflow-wrap:break-word;white-space:nowrap}
.num{text-align:left;font-variant-numeric:tabular-nums;white-space:nowrap}
.bold{font-weight:800}
.red{color:#dc2626}
.green{color:#059669}
.muted{color:#94a3b8;font-size:7px}
.notes{color:#64748b;font-size:7.5px;max-width:90px}
.items-cell{max-width:150px}
.item-row{background:#f1f5f9;border-radius:4px;padding:2px 5px;margin-bottom:2px;font-size:7.5px;border-right:2px solid #cbd5e1;line-height:1.4}
.status{padding:1px 5px;border-radius:20px;font-size:7px;font-weight:700;white-space:nowrap}
.status.paid{background:#d1fae5;color:#059669}
.status.pending{background:#fef3c7;color:#d97706}
/* فوتر */
.footer{margin-top:14px;padding-top:10px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:9.5px;color:#94a3b8}
.sig{border-top:1px dashed #cbd5e1;padding-top:5px;min-width:150px;text-align:center;font-size:10px;color:#475569}
/* حساب‌های نقدی (دخل) */
.cash-wrap{display:flex;gap:10px;flex-wrap:wrap;padding:12px;border:1px solid #e2e8f0;border-top:none}
.cash-card{flex:1;min-width:200px;background:#f0f9ff;border:1px solid #bae6fd;border-radius:10px;padding:10px;text-align:center}
.cash-title{font-size:11px;font-weight:800;color:#0369a1;margin-bottom:8px}
.cash-code{font-size:9px;font-weight:600;color:#64748b;font-family:monospace;margin-right:4px}
.cash-missing{font-size:10px;color:#d97706}
.cash-single{font-size:17px;font-weight:900;border-radius:8px;padding:10px}
.cash-single.pos{background:#ecfdf5;color:#059669;border:1.5px solid #a7f3d0}
.cash-single.neg{background:#fef2f2;color:#dc2626;border:1.5px solid #fecaca}
/* چاپ */
@media print{
  thead{display:table-header-group}
  tr{page-break-inside:avoid}
}
</style></head><body><div class="page">
<div class="header">
  <div style="display:flex;align-items:center;gap:12px">
    <div class="logo-box"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg></div>
    <div><div class="title">گزارش جورنال</div><div class="subtitle">${title}</div></div>
  </div>
  <div class="meta">
    <div>بازه زمانی: <strong>${fromJ}${fromJ !== toJ ? " تا " + toJ : ""}</strong></div>
    <div>تاریخ تهیه: <strong>${gregorianToJalaliStr(todayGregorian())}</strong></div>
    <div>کل معاملات: <strong>${total}</strong></div>
  </div>
</div>
<div class="summary">
  ${[{l:"خرید",c:"#f59e0b",n:report.purchases.length},{l:"فروش",c:"#10b981",n:report.sales.length},{l:"دریافت",c:"#3b82f6",n:report.paymentsIn.length},{l:"پرداخت",c:"#f97316",n:report.paymentsOut.length},{l:"مصارف",c:"#ef4444",n:report.expenses.length},{l:"COGS",c:"#8b5cf6",n:report.cogs.length},{l:"انتقال",c:"#06b6d4",n:report.transfers.length}].map(s=>`<div class="sum-item"><div class="sum-count" style="color:${s.c}">${s.n}</div><div class="sum-label">${s.l}</div></div>`).join("")}
</div>
${sections}
${buildCashSummaryHTML(cashSummary || [])}
<div class="footer">
  <span>سیستم مالی — گزارش جورنال | ${title}</span>
  <div style="display:flex;gap:40px">
    <div class="sig">امضا و مهر مسئول مالی</div>
    <div class="sig">امضا و مهر مدیر</div>
  </div>
</div>
</div></body></html>`;
}